import moment from 'moment';

const MAX_DAILY_HOURS = 24;
const SECONDS_PER_HOUR = 3600;
const MS_PER_HOUR = SECONDS_PER_HOUR * 1000;
// One hundredth of an hour: amounts in whole hundredths read as decimals.
const MS_PER_HUNDREDTH_HOUR = 36_000;

// Single switch point for hours-only reporting. The full report states it
// per viewed account (hoursOnly, true for B2B and civil contracts); the
// contract type is only a fallback for responses without the flag.
export const isHoursOnlyReporting = (
  hoursOnlyFlag?: boolean | null,
  contractType?: string | null,
) =>
  typeof hoursOnlyFlag === 'boolean'
    ? hoursOnlyFlag
    : contractType === 'B2B' || contractType === 'CIVIL_CONTRACT';

export type WorkingHoursParseResult =
  | { valid: true; hours: number | null }
  | { valid: false };

const pad = (value: number) => String(value).padStart(2, '0');

// Accepts 7.5, "7", "7.5", "7,5", "7:30" or "7:20:30" and returns decimal
// hours. Clock amounts are not rounded, so 7:20 is saved as exactly 7 hours
// 20 minutes. An empty value means nothing reported (null); zero is an
// explicit day without work, which counts as reported.
export const parseWorkingHours = (value: unknown): WorkingHoursParseResult => {
  const text =
    typeof value === 'number' ? String(value) : String(value ?? '').trim();
  if (!text || typeof value === 'boolean') {
    return { valid: true, hours: null };
  }
  const clock = text.match(/^(\d{1,2}):([0-5]\d)(?::([0-5]\d))?$/);
  const decimal = text.match(/^(\d{1,2})(?:[.,](\d{1,2}))?$/);
  let hours: number;
  if (clock) {
    hours =
      (Number(clock[1]) * SECONDS_PER_HOUR +
        Number(clock[2]) * 60 +
        Number(clock[3] || 0)) /
      SECONDS_PER_HOUR;
  } else if (decimal) {
    hours = Number(`${decimal[1]}.${decimal[2] || '0'}`);
  } else {
    return { valid: false };
  }
  if (hours > MAX_DAILY_HOURS) {
    return { valid: false };
  }
  return { valid: true, hours };
};

export const getWorkingHours = (value: unknown) => {
  const parsed = parseWorkingHours(value);
  return parsed.valid ? parsed.hours : null;
};

// The saved duration of a row in milliseconds: manHours, exact for
// hours-only rows and for rows still saved with clock times, else the hours
// amount. An empty row has none (null).
export const getSavedMillis = (
  hours?: number | string | null,
  manHoursIsoDuration?: string | null,
) => {
  if (manHoursIsoDuration) {
    return moment.duration(manHoursIsoDuration).asMilliseconds();
  }
  if (hours !== null && hours !== undefined && hours !== '') {
    return Math.round(Number(hours) * MS_PER_HOUR);
  }
  return null;
};

// Text for a duration: whole hundredths of an hour as a decimal (7.5),
// anything else as a clock amount (7:20, or 3:09:37 with seconds).
const formatMillis = (millis: number) => {
  if (millis % MS_PER_HUNDREDTH_HOUR === 0) {
    return String(millis / MS_PER_HUNDREDTH_HOUR / 100);
  }
  const totalSeconds = Math.round(millis / 1000);
  const wholeHours = Math.floor(totalSeconds / SECONDS_PER_HOUR);
  const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / 60);
  const seconds = totalSeconds % 60;
  return seconds
    ? `${wholeHours}:${pad(minutes)}:${pad(seconds)}`
    : `${wholeHours}:${pad(minutes)}`;
};

// Value shown in the hours-only input for the saved duration. A saved
// 0-hour day shows "0", unlike an empty day, which shows only the
// placeholder.
export const formatWorkingHours = (
  hours?: number | string | null,
  manHoursIsoDuration?: string | null,
) => {
  const millis = getSavedMillis(hours, manHoursIsoDuration);
  return millis === null ? '' : formatMillis(millis);
};

// Hours sent for an hours-only row. savedMillis is the saved duration of a
// row whose hours were not edited since the report was loaded: the row clears
// it on the first change. While the input shows it, the exact saved duration
// is sent, so an untouched row keeps its base time to the millisecond; an
// edited row sends the typed value, even when it shows the saved duration.
export const getSubmittedHours = (
  text: unknown,
  savedMillis?: number | string | null,
) => {
  const saved =
    savedMillis === null || savedMillis === undefined || savedMillis === ''
      ? null
      : Number(savedMillis);
  if (
    saved !== null &&
    Number.isFinite(saved) &&
    String(text ?? '').trim() === formatMillis(saved)
  ) {
    return saved / MS_PER_HOUR;
  }
  return getWorkingHours(text);
};
